import React, { useCallback, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DragDropContext } from 'react-beautiful-dnd'
import { DragIndicator } from '@mui/icons-material'
import { Button, AutoComplete, Input } from 'components/Form'
import { CustomSection, XSSpaceBetween } from 'styles/GlobalStyle'
import { getReadabilityScoresFetch } from 'store/middlewares/assessment.crud'
import qc from 'keys/queryClient'
import {
    AssessmentSetStyles,
    WrapList, WrapDroppable, WrapDraggable,
    WrapDragIcon, AssessmentName,
    WrapSectionList,
} from 'pages/Admin/CreateAssessments/Assessments/Style'
import { CreateBatteryContext } from './CreateBatteryContext'
import useCreateBatteryBtn from './useCreateBatteryBtn'

const BatteryForm = () => {
    const {
        selectedAssessmentDefs, setSelectedAssessmentDefs,
        name, setName,
        label, setLabel,
        intendedFor, setIntendedFor,
    } = useContext(CreateBatteryContext)

    const { action, isPending } = useCreateBatteryBtn()

    const { data: readabilityScores } = useQuery({
        queryKey: [qc.readabilityScores],
        queryFn: async () => {
            const { data } = await getReadabilityScoresFetch()
            return data
        }
    })

    const handleOnDragEnd = useCallback((result) => {
        if (!result.destination) return
        const items = Array.from(selectedAssessmentDefs)
        const [reordered] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reordered)
        setSelectedAssessmentDefs(items)
    }, [selectedAssessmentDefs, setSelectedAssessmentDefs])

    const fields = useMemo(() => [
        {
            component: Input,
            placeholder: "שם (אנגלית)",
            input: { onChange: (value) => setName(value) },
            required: true,
            fullWidth: true,
            size: "small",
        },
        {
            component: Input,
            placeholder: "תווית (עברית)",
            input: { onChange: (value) => setLabel(value) },
            fullWidth: true,
            required: true,
            size: "small",
        },
        {
            component: AutoComplete,
            placeholder: "גיל יעד",
            options: readabilityScores || [],
            input: { value: intendedFor, onChange: setIntendedFor },
            required: true,
            fullWidth: true,
            size: "small",
        },
    ], [readabilityScores, intendedFor, setName, setLabel, setIntendedFor])

    const isValid = name.trim() && intendedFor

    return (
        <AssessmentSetStyles>
            <div className='header-set'>
                <div className='title'>בחירת מבדקים לסוללה</div>
            </div>

            <WrapSectionList>
                <CustomSection $bg $grow $flex $border>
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                        <WrapDroppable droppableId="battery-assessments">
                            {(provided) => (
                                <WrapList {...provided.droppableProps} ref={provided.innerRef}>
                                    {selectedAssessmentDefs.map(({ id, name: assName }, index) => (
                                        <WrapDraggable key={id.toString()} draggableId={id.toString()} index={index}>
                                            {(provided) => (
                                                <CustomSection
                                                    $flex align="center" height={50}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <WrapDragIcon>
                                                        <DragIndicator fontSize='small' color="dark" />
                                                    </WrapDragIcon>
                                                    <XSSpaceBetween />
                                                    <AssessmentName>{assName}</AssessmentName>
                                                </CustomSection>
                                            )}
                                        </WrapDraggable>
                                    ))}
                                    {provided.placeholder}
                                </WrapList>
                            )}
                        </WrapDroppable>
                    </DragDropContext>
                </CustomSection>
            </WrapSectionList>

            <div className='footer'>
                <div className='semester-and-year-filters'>
                    {fields.map(({ component: Component, ...field }, i) => (
                        <Component key={i} {...field} />
                    ))}
                </div>
                <div className='create-assessment-btn'>
                    <Button
                        id={1}
                        color="secondary"
                        label="יצירה"
                        disabled={!isValid || isPending}
                        disabledColor="secondary"
                        width="150px"
                        action={action}
                    />
                </div>
            </div>
        </AssessmentSetStyles>
    )
}

export default BatteryForm
